/**
 * @class IntersectionStickyHelper
 * @author Dominik Kadežabek (EN GARDE)
 * 
 * The following functionality allows you to listen for sticky change for CSS position: sticky; 
 * by using IntersectionObserver.
 * 
 * The functionality is inpired by following article: https://developer.chrome.com/blog/sticky-headers/
 * 
 * Glossary:
 * - sticky element - the element that has position: sticky applied
 * - sticky container/parent - direct parent of sticky element
 * 
 * Imporatnet notes:
 * - The top sentinel should have the height set to the space between sticky element and sticky container. If there is no space, then 1px.
 * - The bottom sentinel should usually have the height of sticky element.
 * 
 * Usage:
 * new EN.IntersectionSticky(options)
 * 
 * Options:
 * - options.stickyQuery - mandatory, the query for the position: sticky; element
 * - options.customSentinelClass - optional, used on both sentinels
 * - options.topSentinelHeight - optional, you can use CSS also which is often preferrable because of responsive.
 * - options.bottomSentinelHeight - optional, you can use CSS for it because of responsive resons
 * - options.intersectionRootMargin - rootMargin for IntersectionObserver
 * - options.debugMode - if true shows sentinals for debugging
 */
var EN = EN || {};

EN.IntersectionSticky = (function () {

	var rawSelectors = {
		sentinel: 'en-sticky-helper__sentinel',
		sentinelTop: 'en-sticky-helper__sentinel--top',
		sentinelBottom: 'en-sticky-helper__sentinel--bottom',
	}

	function IntersectionSticky(options) {

		// inputs through options
		this.stickyQuery = options.sticky; // mandatory
		this.customSentinelClass = options.customSentinelClass || undefined;
		this.topSentinelHeight = options.topSentinelHeight || undefined;
		this.bottomSentinelHeight = options.bottomSentinelHeight || undefined;
		this.intersectionRootMargin = options.intersectionRootMargin || null;
		this.debugMode = options.debugMode || false;

		// computed
		this.$sticky = document.querySelectorAll(options.sticky);
		this.$scrollContainer = document.querySelector(options.scrollContainer);
		this.hasSentinels = false;
		this.$topSentinels = [];
		this.$bottomSentinels = [];

		this.addSentinels();
		this.observeSentinels();
	}

	IntersectionSticky.prototype.addSentinels = function () {
		var that = this;

		this.$sticky.forEach(function ($stickyEl) {

			// top sentinel
			var sentinelTop = document.createElement('div');
			sentinelTop.classList.add(rawSelectors.sentinel, rawSelectors.sentinelTop);
			if (that.customSentinelClass) {
				sentinelTop.classList.add(that.customSentinelClass);
			}

			if (that.topSentinelHeight) {
				sentinelTop.style.height = that.topSentinelHeight;
			}

			if (that.debugMode) {
				sentinelTop.style.background = 'yellow';
				sentinelTop.style.opacity = '0.5';
			}

			that.$topSentinels.push(sentinelTop);

			// bottom sentinel
			var sentinelBottom = document.createElement('div');
			sentinelBottom.classList.add(rawSelectors.sentinel, rawSelectors.sentinelBottom);
			if (that.customSentinelClass) {
				sentinelBottom.classList.add(that.customSentinelClass);
			}

			if (that.bottomSentinelHeight) {
				sentinelBottom.style.height = that.bottomSentinelHeight;
			}

			if (that.debugMode) {
				sentinelBottom.style.background = 'yellow';
				sentinelBottom.style.opacity = '0.5';
			}
			that.$bottomSentinels.push(sentinelBottom);

			// add to dom
			$stickyEl.parentElement.append(sentinelTop);
			$stickyEl.parentElement.append(sentinelBottom);
		});

		this.hasSentinels = true;
	};

	IntersectionSticky.fireEvent = function (isSticky, target) {
		var e = new CustomEvent('en/sticky/change', {
			detail: {
				isSticky: isSticky,
				target: target
			}
		});
		document.dispatchEvent(e);
	}

	IntersectionSticky.prototype.observeSentinels = function () {
		var that = this;

		// top sentinel IntersectionObserver instance
		this.topSentinelObserver = new IntersectionObserver(function (entries, observer) {
			for (var i = 0; i < entries.length; i++) {
				var entry = entries[i];

				var targetInfo = entry.boundingClientRect;
				var stickyTarget = entry.target.parentElement.querySelector(that.stickyQuery);
				var rootBoundsInfo = entry.rootBounds;

				// Started sticking.
				if (targetInfo.bottom < rootBoundsInfo.top) {
					IntersectionSticky.fireEvent(true, stickyTarget);
				}

				// Stopped sticking.
				if (targetInfo.bottom >= rootBoundsInfo.top &&
					targetInfo.bottom < rootBoundsInfo.bottom) {
					IntersectionSticky.fireEvent(false, stickyTarget);
				}

			}
		}, { threshold: [0], root: this.$scrollContainer, rootMargin: this.intersectionRootMargin });

		// observe top sentinels
		this.$topSentinels.forEach(function ($el) {
			that.topSentinelObserver.observe($el);
		});

		// bottom sentinel IntersectionObserver instance
		this.bottomSentinelObserver = new IntersectionObserver(function (entries, observer) {
			for (var i = 0; i < entries.length; i++) {
				var entry = entries[i];


				var targetInfo = entry.boundingClientRect;
				var stickyTarget = entry.target.parentElement.querySelector(that.stickyQuery);
				var rootBoundsInfo = entry.rootBounds;
				var ratio = entry.intersectionRatio;

				// Started sticking.
				if (targetInfo.top > rootBoundsInfo.top && ratio === 1) {
					IntersectionSticky.fireEvent(true, stickyTarget);
				}

				// Stopped sticking.
				if (targetInfo.top < rootBoundsInfo.top &&
					targetInfo.bottom < rootBoundsInfo.bottom) {
					IntersectionSticky.fireEvent(false, stickyTarget);
				}

			}
		}, { threshold: [1], root: this.$scrollContainer, rootMargin: this.intersectionRootMargin });

		// observe bottom sentinels
		this.$bottomSentinels.forEach(function ($el) {
			that.bottomSentinelObserver.observe($el);
		});

	}

	IntersectionSticky.prototype.disconnect = function () {
		this.topSentinelObserver.disconnect();
		this.bottomSentinelObserver.disconnect();
	}

	return IntersectionSticky;
}());